
















































































































import { Component, Mixins, Watch } from "vue-property-decorator";
import { PartialNetSuiteCustomer } from "@/models/netsuite/netsuite";
import { namespace } from "vuex-class";
import { StoreModule } from "@/store/types";
import { OrderGetters, OrderActions } from "@/store/modules/order/types";
import { LineItem, Order } from "@/models/order/order";
import { GlobalActions } from "@/store/modules/global/types";
import ConfirmNewQuote from "@/components/elements/modal/ConfirmNewQuote.vue";
import ConfirmClearCustomer from "@/components/elements/modal/ConfirmClearCustomer.vue";
import AssignSavedQuote from "@/components/elements/modal/AssignSavedQuote.vue";
import HeroIcon from "@/components/elements/icons/HeroIcon.vue";
import { CustomerMixin } from "@/mixins/customers/mixin";

@Component({
  components: {
    ConfirmNewQuote,
    ConfirmClearCustomer,
    AssignSavedQuote,
    HeroIcon
  }
})
export default class CustomerDropdown extends Mixins(CustomerMixin) {
  @(namespace(StoreModule.Order).Getter(OrderGetters.GetAssignedCustomer))
  customer!: string;
  @(namespace(StoreModule.Order).Getter(OrderGetters.GetOrderItems))
  items!: LineItem[];
  @(namespace(StoreModule.Order).Getter(OrderGetters.GetOrder))
  order!: Order;
  @(namespace(StoreModule.Order).Action(OrderActions.ClearCustomer))
  clearCustomer!: () => Promise<void>;

  get activeCustomer(): PartialNetSuiteCustomer | undefined {
    if (!this.customer) return undefined;
    return this.customers.find(c => c.company_name === this.customer);
  }

  protected showModalClearCustomer = false;
  protected showModalAssignCustomer = false;
  protected showInlineClearConfirm = false;
  protected newCustomer: PartialNetSuiteCustomer = {
    customer_id: 0,
    company_name: "New Customer",
    payment_terms_id: 0,
    payment_terms: "",
    price_level_id: 0,
    price_level_label: "",
    price_level_discount: 0,
    invoice_email: "",
    default_billing_address: "",
    default_shipping_address: "",
    on_credit_hold: "F",
    credit_limit: null,
    balance: 0,
    overdue_balance: 0,
    unbilled_orders_balance: 0,
    sales_rep_id: 0,
    sales_rep: ""
  };

  async created() {
    await this.getCustomers();
    const newCustomerId = this.$route.query.newCustomerId;
    if (newCustomerId) {
      const id = parseInt(newCustomerId as string, 10);
      const match = this.customers.find(c => c.customer_id === id);
      if (match) {
        this.toggleNewQuoteModal(match);
      }
    }
  }

  protected formatHeaders(header: string): string {
    let formatted = "";
    switch (header) {
      case "store_name":
        formatted = "Company Name";
        break;
      case "sales_contacts":
        formatted = "Contact";
        break;
      default:
        formatted = this.makeTitleCase(header);
        break;
    }
    return formatted;
  }

  protected async toggleNewQuoteModal(customer: PartialNetSuiteCustomer): Promise<void> {
    this.setSelectedCustomer(
      customer.customer_id.toString(),
      customer.company_name,
      1 + (customer.price_level_discount ?? 0),
      customer.customer_id || undefined,
      customer.payment_terms_id || undefined,
      customer.price_level_id || undefined,
      customer.price_level_discount ?? undefined
    );
    if (this.order._id) {
      this.showModalAssignCustomer = true;
    } else {
      await this.reassignQuote(
        this.selected?.customer_id!,
        this.selected?.customer_name!,
        this.selected?.multiplier!,
        this.selected?.customer_netsuite_id,
        this.selected?.customer_netsuite_payment_terms_id,
        this.selected?.customer_netsuite_price_level_id,
        this.selected?.customer_netsuite_price_level_discount
      );
    }
  }

  protected async createNewCustomer(): Promise<void> {
    await this.reassignQuote(
      "0",
      "New Customer",
      1,
      undefined,
      undefined,
      undefined,
      undefined
    );
  }

  protected toggleClearCustomer(): void {
    this.showModalClearCustomer = true;
  }

  protected async assignSavedQuote() {
    const id = this.selected?.customer_id!;
    const name = this.selected?.customer_name!;
    const multiplier = this.selected?.multiplier!;
    const netsuite_id = this.selected?.customer_netsuite_id;
    const netsuite_payment_terms_id = this.selected
      ?.customer_netsuite_payment_terms_id;
    const netsuite_price_level_id = this.selected
      ?.customer_netsuite_price_level_id;
    const netsuite_price_level_discount = this.selected
      ?.customer_netsuite_price_level_discount;
    const oid = this.order._id;
    await this.saveCustomerToStore(
      id,
      name,
      multiplier,
      netsuite_id,
      netsuite_payment_terms_id,
      netsuite_price_level_id,
      netsuite_price_level_discount,
      oid
    );
    this.close(); // sets selected to null, must be called last
  }

  protected formatAddress(address: string): string {
    const lines = address.includes("\n")
      ? address.split("\n")
      : address.split(/,\s*/);
    // Find the city/state/zip line: matches "City ST 12345" or "City State 12345"
    const cityStateZipRegex = /^(.+?)\s+([A-Z]{2})\s+(\d{5}(-\d{4})?)$/;
    return lines
      .map(line => {
        const match = line.trim().match(cityStateZipRegex);
        if (match) {
          return `${match[1]}, ${match[2]} ${match[3]}`;
        }
        return line.trim();
      })
      .join("\n");
  }

  protected async inlineClearCustomer(): Promise<void> {
    await this.clearCustomer();
    const baseLevel = this.storePriceLevels.find(l => l.id === 1) ?? null;
    await this.addMultiplier(baseLevel);
    this.showInlineClearConfirm = false;
    this.close();
  }

  protected async inlineClearAll(): Promise<void> {
    await this.resetOrder();
    await this.clearCustomer();
    const baseLevel = this.storePriceLevels.find(l => l.id === 1) ?? null;
    await this.addMultiplier(baseLevel);
    this.showInlineClearConfirm = false;
    this.close();
  }

  protected close(): void {
    this.showModal = false;
    this.showModalClearCustomer = false;
    this.showModalAssignCustomer = false;
    this.showInlineClearConfirm = false;
    this.selected = null;
    this.searchTerm = "";
    this.$emit("close");
  }
}
